import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";
import { AuthClient, type AuthConfig, type AuthState } from "@stenpro/auth";

interface AuthContextValue {
  client: AuthClient;
  config: AuthConfig;
  state: AuthState;
  login: AuthClient["loginWithRedirect"];
  logout: AuthClient["logout"];
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export interface AuthProviderProps extends PropsWithChildren {
  config: AuthConfig;
}

export function AuthProvider({ children, config }: AuthProviderProps) {
  const client = useMemo(() => new AuthClient(config), [config]);
  const [state, setState] = useState<AuthState>(client.getState());

  useEffect(() => client.onStateChange(setState), [client]);

  const value = useMemo<AuthContextValue>(() => ({
    client,
    config,
    state,
    login: client.loginWithRedirect.bind(client),
    logout: client.logout.bind(client),
  }), [client, config, state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }

  return context;
}
